/**
 * This is a wrapper component for different Layouts.
 * Navigational 'aside' content should be added to this wrapper.
 */
import React, { useEffect, useState } from 'react';
import { node, number, string, shape } from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';

import { FormattedMessage } from '../../../util/reactIntl';
import { withViewport } from '../../../util/uiHelpers';
import { ensureCurrentUser } from '../../../util/data';
import { apiBaseUrl } from '../../../util/api';

import { TabNav } from '../../../components';

import { createGlobalState } from './hookGlobalState';

import css from './LayoutSideNavigation.module.css';
import { useIntl } from 'react-intl';

const MAX_HORIZONTAL_NAV_SCREEN_WIDTH = 1023;

// Add global state for tab scrolling effect
const initialScrollState = { scrollLeft: 0 };
const { useGlobalState } = createGlobalState(initialScrollState);

// Horizontal scroll animation using element.scrollTo()
const scrollToTab = (currentPage, scrollLeft, setScrollLeft) => {
  const el = document.querySelector(`#${currentPage}Tab`);

  if (el) {
    // el.scrollIntoView doesn't work with Safari and it considers vertical positioning too.
    // This scroll behaviour affects horizontal scrolling only
    // and it expects that the immediate parent element is scrollable.
    const parent = el.parentElement;
    const parentRect = parent.getBoundingClientRect();
    const maxScrollDistance = parent.scrollWidth - parentRect.width;

    const hasParentScrolled = parent.scrollLeft > 0;
    const scrollPositionCurrent = hasParentScrolled ? parent.scrollLeft : scrollLeft;

    const tabRect = el.getBoundingClientRect();
    const diffLeftBetweenTabAndParent = tabRect.left - parentRect.left;
    const tabScrollPosition = parent.scrollLeft + diffLeftBetweenTabAndParent;

    const scrollPositionNew =
      tabScrollPosition > maxScrollDistance
        ? maxScrollDistance
        : parent.scrollLeft + diffLeftBetweenTabAndParent;

    const needsSmoothScroll = scrollPositionCurrent !== scrollPositionNew;

    if (!hasParentScrolled || (hasParentScrolled && needsSmoothScroll)) {
      // Ensure that smooth scroll animation uses old position as starting point after navigation.
      parent.scrollTo({ left: scrollPositionCurrent });
      // Scroll to new position
      parent.scrollTo({ left: scrollPositionNew, behavior: 'smooth' });
    }
    // Always keep track of new position (even if smooth scrolling is not applied)
    setScrollLeft(scrollPositionNew);
  }
};

const LayoutWrapperAccountSettingsSideNavComponent = props => {
  const { currentPage, viewport, currentUser } = props;

  const [scrollLeft, setScrollLeft] = useGlobalState('scrollLeft');

  const intl = useIntl();
  
  // Get userIntent from currentUser
  const ensuredCurrentUser = ensureCurrentUser(currentUser);
  const userIntent = ensuredCurrentUser?.attributes?.profile?.privateData?.userIntent || null;

  // Check if user is a company admin
  const currentUserId = currentUser?.id?.uuid || null;
  const [isCompanyAdmin, setIsCompanyAdmin] = useState(false);
  useEffect(() => {
    if (currentUserId) {
      fetch(`${apiBaseUrl()}/api/companies/my`, { credentials: 'include' })
        .then(res => {
          setIsCompanyAdmin(res.ok);
        })
        .catch(() => {
          setIsCompanyAdmin(false);
        });
    } else {
      setIsCompanyAdmin(false);
    }
  }, [currentUserId]);

  useEffect(() => {
    let scrollTimeout = null;

    const { width } = viewport;
    const hasViewport = width > 0;
    const hasHorizontalTabLayout = hasViewport && width <= MAX_HORIZONTAL_NAV_SCREEN_WIDTH;

    // Check if scrollToTab call is needed (tab is not visible on mobile)
    if (hasHorizontalTabLayout) {
      scrollTimeout = window.setTimeout(() => {
        scrollToTab(currentPage, scrollLeft, setScrollLeft);
      }, 300);
    }

    return () => {
      // Update scroll position when unmounting
      const el = document.querySelector(`#${currentPage}Tab`);
      setScrollLeft(el.parentElement.scrollLeft);
      if (scrollTimeout) {
        clearTimeout(scrollTimeout);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewport]);

  const tabs = [
    {
      text: <FormattedMessage id="LayoutWrapperAccountSettingsSideNav.contactDetailsTabTitle" />,
      selected: currentPage === 'ContactDetailsPage',
      id: 'ContactDetailsPageTab',
      linkProps: {
        name: 'ContactDetailsPage',
      },
    },
    {
      text: <FormattedMessage id="LayoutWrapperAccountSettingsSideNav.profile" />,
      selected: currentPage === 'ProfileSettingsPage',
      id: 'ProfileSettingsPageTab',
      linkProps: {
        name: 'ProfileSettingsPage',
      },
    },
    {
      text: <FormattedMessage id="LayoutWrapperAccountSettingsSideNav.passwordTabTitle" />,
      selected: currentPage === 'PasswordChangePage',
      id: 'PasswordChangePageTab',
      linkProps: {
        name: 'PasswordChangePage',
      },
    },
  ];
  
  // Add Payouts tab only for providers
  if (userIntent === 'provider') {
    tabs.push({
      text: <FormattedMessage id="LayoutWrapperAccountSettingsSideNav.paymentsTabTitle" />,
      selected: currentPage === 'StripePayoutPage',
      id: 'StripePayoutPageTab',
      linkProps: {
        name: 'StripePayoutPage',
      },
    });
  }
  
  // Add Payments tab only for customers
  if (userIntent === 'customer') {
    tabs.push({
      text: <FormattedMessage id="LayoutWrapperAccountSettingsSideNav.paymentMethodsTabTitle" />,
      selected: currentPage === 'PaymentMethodsPage',
      id: 'PaymentMethodsPageTab',
      linkProps: {
        name: 'PaymentMethodsPage',
      },
    });
  }
  
  tabs.push(
    {
      text: <FormattedMessage id="General.calendar" />,
      selected: currentPage === 'CalendarPage',
      id: 'CalendarPageTab',
      linkProps: {
        name: 'CalendarPage',
      },
    },
    {
      text: <FormattedMessage id="General.promotions" />,
      selected: currentPage === 'PromotionsPage',
      id: 'PromotionsPageTab',
      linkProps: {
        name: 'PromotionsPage',
      },
    }
  );

  // Add Company Dashboard tab for company admins
  if (isCompanyAdmin) {
    tabs.push({
      text: <FormattedMessage id="LayoutWrapperAccountSettingsSideNav.companyDashboard" />,
      selected: currentPage === 'CorporateDashboardPage',
      id: 'CorporateDashboardPageTab',
      linkProps: {
        name: 'CorporateDashboardPage',
      },
    });
  }

  return (
    <TabNav
      rootClassName={css.tabs}
      tabRootClassName={css.tab}
      navRootClassName={css.nav}
      tabsRootClassName={css.navTabs}
      tabs={tabs}
      navTitle={intl.formatMessage({
        id: 'LayoutWrapperAccountSettingsSideNav.yourAccountNavTitle',
      })}
    />
  );
};

LayoutWrapperAccountSettingsSideNavComponent.defaultProps = {
  className: null,
  rootClassName: null,
  children: null,
  currentPage: null,
  currentUser: null,
};

LayoutWrapperAccountSettingsSideNavComponent.propTypes = {
  children: node,
  className: string,
  rootClassName: string,
  currentPage: string,
  currentUser: shape({}),

  // from withViewport
  viewport: shape({
    width: number.isRequired,
    height: number.isRequired,
  }).isRequired,
};

const mapStateToProps = state => {
  const { currentUser } = state.user;
  return {
    currentUser,
  };
};

const LayoutWrapperAccountSettingsSideNav = compose(
  connect(mapStateToProps),
  withViewport
)(LayoutWrapperAccountSettingsSideNavComponent);

export default LayoutWrapperAccountSettingsSideNav;
